/**
 * Difficulty Detection — deterministic, metrics-driven.
 * Produces difficulty_score (0–100) and difficulty_level from user-card progress.
 */

import type { UserCardProgress } from "./types";
import type { DifficultyLevel } from "./types";
import { DIFFICULTY } from "./constants";

/**
 * Compute difficulty score (0–100) from progress.
 * Weighted combination: error rate, recent failures, latency, hint usage, streak.
 * Recent performance is weighted more (exponential decay).
 */
export function computeDifficultyScore(progress: UserCardProgress): number {
  const seen = progress.timesSeen;
  if (seen === 0) return DIFFICULTY.SCORE_MAX / 2; // neutral for new cards

  const incorrect = progress.timesIncorrect;
  const correct = progress.timesCorrect;

  // 1. Error rate: incorrect / seen (0–1 -> 0–100)
  const errorRate = seen > 0 ? incorrect / seen : 0;
  const errorScore = Math.min(100, errorRate * 100);

  // 2. Recent failures: approximate from last_correct vs last_incorrect and streak
  const lastCorrect = progress.lastCorrectAt ? new Date(progress.lastCorrectAt).getTime() : 0;
  const lastIncorrect = progress.lastIncorrectAt ? new Date(progress.lastIncorrectAt).getTime() : 0;
  const lastFailMoreRecent = lastIncorrect > lastCorrect;
  const recentFailScore = lastFailMoreRecent ? 70 : (progress.currentStreak === 0 && seen > 1 ? 50 : 20);

  // 3. Latency: slow response = slightly harder (normalize ~0–5s -> 0–30)
  const avgMs = progress.avgResponseMs ?? progress.lastResponseMs ?? 0;
  const latencyScore = avgMs <= 0 ? 0 : Math.min(30, Math.floor(avgMs / 200));

  // 4. Hint/explain usage
  const hintExplainTotal = progress.hintUsedCount + progress.explainUsedCount;
  const hintScore = Math.min(40, hintExplainTotal * 8);

  // 5. Streak: low or broken = harder
  const bestStreak = progress.bestStreak || 1;
  const streakRatio = progress.currentStreak / bestStreak;
  const streakScore = streakRatio >= 1 ? 0 : (1 - streakRatio) * 60;

  const score =
    DIFFICULTY.WEIGHT_ERROR_RATE * errorScore +
    DIFFICULTY.WEIGHT_RECENT_FAILURES * recentFailScore +
    DIFFICULTY.WEIGHT_LATENCY * latencyScore +
    DIFFICULTY.WEIGHT_HINT_EXPLAIN * hintScore +
    DIFFICULTY.WEIGHT_STREAK * streakScore;

  return Math.round(Math.max(DIFFICULTY.SCORE_MIN, Math.min(DIFFICULTY.SCORE_MAX, score)));
}

/**
 * Map numeric difficulty score to level.
 */
export function difficultyScoreToLevel(score: number): DifficultyLevel {
  if (score <= DIFFICULTY.LEVEL_BANDS.easy) return "easy";
  if (score <= DIFFICULTY.LEVEL_BANDS.medium) return "medium";
  if (score <= DIFFICULTY.LEVEL_BANDS.hard) return "hard";
  return "critical";
}

/**
 * Full difficulty metrics from progress.
 */
export function computeDifficultyMetrics(progress: UserCardProgress): {
  difficultyScore: number;
  difficultyLevel: DifficultyLevel;
} {
  const difficultyScore = computeDifficultyScore(progress);
  const difficultyLevel = difficultyScoreToLevel(difficultyScore);
  return { difficultyScore, difficultyLevel };
}
